import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Check, ChevronDown, Pencil, Search, Star, Trash2, TrendingUp, Zap } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import useToast from "../../hooks/useToast";
import Toast from "../../components/ui/Toast";
import ConfirmModal from "../../components/modals/ConfirmModal";
import PageWrapper from "../../components/layout/PageWrapper";
import { isAdmin } from "../../utils/permissions";
import { apiErrorMessage, boardService, boardTaskService, sprintService, squadService } from "../../api/taskBoardService";
import { SquadFormModal } from "../../components/task-board/CatalogFormModals";
import TaskFormModal from "../../components/task-board/TaskFormModal";
import SquadMembers from "../../components/task-board/SquadMembers";
import StatusBar from "../../components/task-board/StatusBar";
import { ErrorState, SkeletonRows, formatDate } from "../../components/task-board/shared";
import { currentSprint } from "../../components/task-board/boardState";
import { ALL_SPRINTS, LAST_3, sortSprints, SQUAD_STATUSES, inScope, insights, previousAverage, scopeIds, summarize, velocity } from "../../components/task-board/squadStats";

const Card = ({ children, className = "" }) => (
  <section
    className={`min-w-0 ${className}`}
    style={{ background: "var(--panel)", border: "1px solid var(--line)", borderRadius: 18, padding: 20, backdropFilter: "blur(20px)", boxShadow: "var(--card-shadow)" }}
  >
    {children}
  </section>
);

const CardHead = ({ title, right }) => (
  <div className="flex items-center justify-between gap-3 mb-4">
    <h2 className="font-display text-[15px] font-semibold text-ink whitespace-nowrap">{title}</h2>
    {right}
  </div>
);

const Kpi = ({ label, value, sub, color = "var(--text)" }) => (
  <Card>
    <p className="uppercase text-faint whitespace-nowrap" style={{ fontSize: 11.5, letterSpacing: ".06em" }}>{label}</p>
    <p className="font-display mt-2" style={{ fontSize: 32, lineHeight: 1.1, fontVariantNumeric: "tabular-nums", color }}>{value}</p>
    <p className="text-[12.5px] text-muted mt-1">{sub}</p>
  </Card>
);

const range = (s) =>
  s.startDate ? `${formatDate(s.startDate, { day: "numeric", month: "short" })} – ${formatDate(s.endDate, { day: "numeric", month: "short" })}` : "";

const ScopeMenu = ({ sprints, current, scope, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => !ref.current?.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const n = sprints.length;
  const pick = (value) => {
    onChange(value);
    setOpen(false);
    setQuery("");
  };

  const q = query.trim().toLowerCase();
  const matches = [...sprints]
    .map((s, i) => ({ s, number: i + 1 }))
    .reverse()
    .filter(({ s, number }) => !q || s.name.toLowerCase().includes(q) || String(number) === q);

  const single = sprints.find((s) => s._id === scope);
  const label =
    scope === ALL_SPRINTS ? "All sprints" : scope === LAST_3 ? "Last 3 sprints" : `${single?.name || "Sprint"}${single?._id === current?._id ? " · Current" : ""}`;
  const last = sprints[n - 1];
  const preset = (value, title, sub) => (
    <button
      type="button"
      role="option"
      aria-selected={scope === value}
      onClick={() => pick(value)}
      className="w-full flex items-center justify-between gap-2 text-left px-3 py-2 rounded-lg hover:bg-hover"
      style={scope === value ? { background: "var(--accent-soft)" } : undefined}
    >
      <span className="min-w-0">
        <span className="block text-[13.5px] text-ink truncate">{title}</span>
        <span className="block text-[11.5px] text-faint truncate">{sub}</span>
      </span>
      {scope === value && <Check className="w-4 h-4 shrink-0" style={{ color: "var(--accent)" }} />}
    </button>
  );

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 text-[13.5px] px-3.5 py-2 rounded-xl whitespace-nowrap"
        style={{ background: "var(--accent-soft)", border: "1px solid var(--accent-line)", color: "var(--accent-ink)" }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Zap className="w-4 h-4" /> <b>{label}</b> <ChevronDown className="w-4 h-4" />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 z-30 overflow-hidden"
          style={{ width: 280, background: "var(--panel-solid)", border: "1px solid var(--line2)", borderRadius: 14, boxShadow: "var(--shadow)" }}
        >
          <div role="listbox" style={{ padding: 6, borderBottom: "1px solid var(--line)" }}>
            {preset(ALL_SPRINTS, "All sprints", n ? `${sprints[0].name} – ${(current || last).name} · ${n} sprints` : "No sprints")}
            {preset(LAST_3, "Last 3 sprints", n ? `${sprints[Math.max(0, (current ? sprints.indexOf(current) : n - 1) - 2)].name} – ${(current || last).name}` : "No sprints")}
          </div>
          <div style={{ padding: 6 }}>
            <label className="flex items-center gap-2 px-2.5" style={{ height: 34, borderRadius: 9, background: "var(--panel2)" }}>
              <Search className="w-4 h-4 text-faint shrink-0" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Find a sprint…"
                className="flex-1 min-w-0 bg-transparent outline-none text-[13px] text-ink placeholder:text-faint"
                aria-label="Find a sprint"
              />
            </label>
          </div>
          <ul role="listbox" className="px-1.5 pb-1.5" style={{ maxHeight: 260, overflowY: "auto" }}>
            {matches.length === 0 && <li className="px-3 py-3 text-[13px] text-faint">No sprint matches</li>}
            {matches.map(({ s }) => (
              <li key={s._id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={s._id === scope}
                  onClick={() => pick(s._id)}
                  className="w-full flex items-center justify-between gap-2 text-left px-3 py-2 rounded-lg hover:bg-hover"
                  style={s._id === scope ? { background: "var(--accent-soft)" } : undefined}
                >
                  <span className="min-w-0">
                    <span className="block text-[13.5px] text-ink truncate">{s.name}</span>
                    <span className="block text-[11.5px] text-faint truncate">{s._id === current?._id ? `Current · ${range(s)}` : range(s)}</span>
                  </span>
                  {s._id === scope && <Check className="w-4 h-4 shrink-0" style={{ color: "var(--accent)" }} />}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const Insight = ({ icon: Icon, color, label, value, sub, valueColor }) => (
  <div className="flex items-center gap-3">
    <span className="grid place-items-center shrink-0" style={{ width: 36, height: 36, borderRadius: 10, background: `${color}22`, color }}>
      <Icon className="w-[18px] h-[18px]" />
    </span>
    <div className="min-w-0">
      <p className="text-[12px] text-faint">{label}</p>
      <p className="text-[14.5px] font-medium truncate" style={{ color: valueColor || "var(--text)" }}>{value}</p>
      <p className="text-[12px] text-muted truncate">{sub}</p>
    </div>
  </div>
);

const sprintNumber = (sprint, index) => sprint.name.match(/\d+/)?.[0] || index + 1;

const SquadDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { auth } = useAuth();
  const admin = isAdmin(auth?.employee);
  const [squad, setSquad] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [scope, setScope] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [taskModal, setTaskModal] = useState(null); // { task, options }
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { toast, showError, showSuccess, hideToast } = useToast();

  const load = useCallback(async () => {
    try {
      const [sq, sp, tk] = await Promise.all([squadService.get(id), sprintService.list(), boardTaskService.list({ squad: id })]);
      setSquad(sq.data);
      setSprints(sp.data);
      setTasks(tk.data);
      setError("");
    } catch (err) {
      setError(apiErrorMessage(err, "Could not load the squad"));
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const current = useMemo(() => currentSprint(sprints), [sprints]);
  const sprintList = useMemo(() => sortSprints(sprints), [sprints]);
  const activeScope = scope || current?._id || ALL_SPRINTS;
  const ids = useMemo(() => scopeIds(activeScope, sprintList, current?._id), [activeScope, sprintList, current]);
  // Overdue only counts when the scope includes the current sprint.
  const overdueId = current && ids.has(current._id) ? current._id : null;
  const scopeTasks = useMemo(() => inScope(tasks, ids), [tasks, ids]);
  const stats = useMemo(() => summarize(scopeTasks, overdueId), [scopeTasks, overdueId]);
  const bars = useMemo(() => velocity(sprintList, tasks, ids, current?._id), [sprintList, tasks, ids, current]);
  const maxTasks = Math.max(1, ...bars.map((b) => b.tasks));
  const insight = useMemo(
    () => (squad ? insights({ members: squad.members, scopeTasks, squadTasks: tasks, sprintList, current }) : null),
    [squad, scopeTasks, tasks, sprintList, current],
  );
  const average = useMemo(() => (current ? previousAverage(tasks, sprintList, current).average : 0), [tasks, sprintList, current]);

  const scopeName =
    activeScope === ALL_SPRINTS ? "All sprints" : activeScope === LAST_3 ? "Last 3 sprints" : sprintList.find((s) => s._id === activeScope)?.name;
  const scopeSub = ids.size > 1 ? `across ${ids.size} sprints` : `in ${scopeName}`;
  const pace = insight?.pace;

  const openTask = useCallback(
    async (task) => {
      try {
        setTaskModal({ task, options: (await boardService.getOptions()).data });
      } catch (err) {
        showError(apiErrorMessage(err, "Could not open the task"));
      }
    },
    [showError],
  );

  return (
    <>
      <PageWrapper
        title={<span style={{ fontSize: 34, fontWeight: 600, letterSpacing: "-.035em" }}>{squad?.name || "Squad"}</span>}
        subtitle={squad?.description && <span className="block max-w-[760px]" style={{ textWrap: "pretty" }}>{squad.description}</span>}
        actions={
          squad && (
            <>
              <ScopeMenu sprints={sprintList} current={current} scope={activeScope} onChange={setScope} />
              {admin && (
                <>
                  <button type="button" onClick={() => setEditing(true)} className="inline-flex items-center gap-1.5 text-[13.5px] px-3.5 py-2 rounded-xl text-ink" style={{ border: "1px solid var(--line2)" }}>
                    <Pencil className="w-4 h-4" /> Edit
                  </button>
                  <button type="button" onClick={() => setConfirmDelete(true)} className="inline-flex items-center gap-1.5 text-[13.5px] px-3.5 py-2 rounded-xl" style={{ background: "var(--red-soft)", color: "var(--red)" }}>
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </>
              )}
            </>
          )
        }
      >
        <Link to="/task-board/squads" className="inline-flex items-center gap-1.5 text-sm mb-4 text-muted hover:text-ink">
          <ArrowLeft className="w-4 h-4" /> All squads
        </Link>

        {error ? (
          <ErrorState message={error} onRetry={load} />
        ) : !squad ? (
          <SkeletonRows rows={4} />
        ) : (
          <div className="space-y-4">
            <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
              <Kpi label="Tasks" value={stats.tasks} sub={scopeSub} />
              <Kpi label="Done" value={stats.done} sub={`${stats.pct}% completion`} color="var(--green)" />
              <Kpi label="In flight" value={stats.inFlight} sub="Ready → QA" color="var(--accent-ink)" />
              <Kpi label="Overdue" value={stats.overdue} sub={stats.overdue ? "past their deadline" : "nothing late"} color={stats.overdue > 0 ? "var(--red)" : "var(--muted)"} />
            </div>

            <Card>
              <CardHead title="Status breakdown" right={<span className="text-[12.5px] text-muted whitespace-nowrap">{stats.tasks} tasks · {scopeName}</span>} />
              <StatusBar byStatus={stats.byStatus} total={stats.tasks} height={12} titled />
              <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4">
                {SQUAD_STATUSES.map((s) => (
                  <span key={s.value} className="inline-flex items-center gap-2 text-[13px] text-muted" style={{ opacity: stats.byStatus[s.value] ? 1 : 0.45 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: s.color }} />
                    {s.label} <b className="text-ink">{stats.byStatus[s.value]}</b>
                  </span>
                ))}
              </div>
            </Card>

            <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 360px), 1fr))" }}>
              <Card>
                <CardHead
                  title={`Tasks per sprint · ${sprintList.length} sprints`}
                  right={
                    <span className="flex items-center gap-3 text-[12px] text-muted">
                      <span className="inline-flex items-center gap-1.5"><i style={{ width: 10, height: 10, borderRadius: 3, background: "var(--raise)", border: "1px solid var(--line)" }} /> Planned</span>
                      <span className="inline-flex items-center gap-1.5"><i style={{ width: 10, height: 10, borderRadius: 3, background: "var(--grad)" }} /> Done</span>
                    </span>
                  }
                />
                <div style={{ overflowX: "auto" }}>
                  <div
                    className="relative"
                    style={{ display: "grid", gridAutoFlow: "column", gridAutoColumns: "minmax(38px, 1fr)", gap: 10, height: 180 }}
                  >
                    {bars.map((b, i) => (
                      <div
                        key={b.sprint._id}
                        title={`${b.sprint.name} · ${b.done} / ${b.tasks} done`}
                        className="flex flex-col items-center min-w-0"
                        style={{
                          gap: 6,
                          opacity: b.inScope ? 1 : 0.4,
                        }}
                      >
                        <span className="text-ink tabular-nums" style={{ height: 16, lineHeight: "16px", fontSize: 11.5, fontWeight: 600, whiteSpace: "nowrap" }}>{b.done} / {b.tasks}</span>
                        <div className="flex items-end justify-center w-full" style={{ height: "calc(100% - 44px)", maxWidth: 44, flexShrink: 0 }}>
                          <div
                            className="relative w-full overflow-hidden"
                            style={{ height: `${(b.tasks / maxTasks) * 100}%`, background: "var(--raise)", borderRadius: "8px 8px 3px 3px" }}
                          >
                            <div className="absolute bottom-0 left-0 right-0" style={{ height: `${b.tasks ? (b.done / b.tasks) * 100 : 0}%`, background: "var(--grad)" }} />
                          </div>
                        </div>
                        <span className="text-faint whitespace-nowrap" style={{ height: 16, lineHeight: "16px", fontSize: 11 }}>S{sprintNumber(b.sprint, i)}</span>
                      </div>
                    ))}
                    {average > 0 && (
                      <div className="absolute left-0 right-0 pointer-events-none" style={{ bottom: 22, height: "calc(100% - 44px)" }}>
                        <div className="absolute left-0 right-0 bottom-0" style={{ height: `${(average / maxTasks) * 100}%`, borderTop: "1px dashed var(--accent-line)" }}>
                          <span className="absolute right-0 px-1 whitespace-nowrap" style={{ top: -9, fontSize: 11, color: "var(--accent-ink)", background: "var(--panel-solid)" }}>
                            avg {average.toFixed(1)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Card>

              <Card>
                <CardHead title="Insights" />
                <div className="space-y-5">
                  <Insight
                    icon={Star}
                    color="#FACC15"
                    label="Top contributor"
                    value={insight?.topContributor?.employee.fullNameEn || "—"}
                    sub={insight?.topContributor ? `${insight.topContributor.done} done · ${scopeName}` : "No finished tasks yet"}
                  />
                  <Insight
                    icon={AlertTriangle}
                    color="#FF8A1F"
                    label="Most open work"
                    value={insight?.mostOpen?.employee.fullNameEn || "—"}
                    sub={insight?.mostOpen ? `${insight.mostOpen.open} open · watch for overload` : "Nothing open"}
                  />
                  <Insight
                    icon={TrendingUp}
                    color="var(--accent)"
                    label={`${current?.name || "Sprint"} pace`}
                    value={pace?.delta == null ? "—" : `${pace.delta >= 0 ? "+" : ""}${pace.delta}%`}
                    valueColor={pace?.delta == null ? undefined : pace.delta >= 0 ? "var(--green)" : "var(--red)"}
                    sub={
                      pace && pace.previousCount
                        ? `${pace.currentDone} done vs. the ${pace.previousCount}-sprint average (${pace.average.toFixed(1)})`
                        : "No earlier sprints to compare"
                    }
                  />
                </div>
              </Card>
            </div>

            <SquadMembers
              squad={squad}
              admin={admin}
              onError={showError}
              onChanged={(updated) => {
                setSquad((prev) => ({ ...prev, ...updated }));
                showSuccess("Members updated");
              }}
              squadTasks={tasks}
              scopeTasks={scopeTasks}
              sprintList={sprintList}
              scopeIds={ids}
              currentId={current?._id}
              overdueId={overdueId}
              scopeName={scopeName}
              onOpenTask={openTask}
            />
          </div>
        )}
      </PageWrapper>

      {squad && (
        <SquadFormModal
          isOpen={editing}
          squad={squad}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      )}
      {taskModal && (
        <TaskFormModal
          isOpen
          task={taskModal.task}
          options={taskModal.options}
          onClose={() => setTaskModal(null)}
          onConflict={() => {
            setTaskModal(null);
            load();
          }}
          onSaved={(_task, message) => {
            setTaskModal(null);
            load();
            showSuccess(message);
          }}
          onDeleted={() => {
            setTaskModal(null);
            load();
            showSuccess("Task deleted");
          }}
        />
      )}
      <ConfirmModal
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={async () => {
          setConfirmDelete(false);
          try {
            await squadService.remove(id);
            navigate("/task-board/squads", { replace: true });
          } catch (err) {
            showError(apiErrorMessage(err));
          }
        }}
        title="Delete squad?"
        message="This permanently deletes the squad. It is only allowed when no task uses it."
        confirmText="Delete permanently"
      />
      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </>
  );
};

export default SquadDetails;
